'use client'
import React, { useState, useRef, useEffect } from 'react'
import styles from './Watchlist.module.css'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface FavoritesPopupProps {
  isOpen: boolean
  onClose: () => void
  onAddFavorite: (symbol: string) => void
  currentFavorites: string[]
  availableSymbols?: string[] // Optional list of available symbols
}

export function FavoritesPopup({
  isOpen,
  onClose,
  onAddFavorite,
  currentFavorites,
  availableSymbols = []
}: FavoritesPopupProps) {
  const [symbol, setSymbol] = useState('')
  const [filteredSymbols, setFilteredSymbols] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter available symbols based on input
  useEffect(() => {
    if (symbol.trim() === '') {
      setFilteredSymbols([])
      setSelectedIndex(-1)
      return
    }

    const upperSymbol = symbol.toUpperCase()
    const filtered = availableSymbols
      .filter(s => s.includes(upperSymbol) && !currentFavorites.includes(s))
      .slice(0, 10) // Limit to 10 results

    setFilteredSymbols(filtered)
    setSelectedIndex(filtered.length > 0 ? 0 : -1)
  }, [symbol, availableSymbols, currentFavorites])

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Focus the input when popup opens
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredSymbols.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < filteredSymbols.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        if (selectedIndex >= 0) {
          handleSymbolSelect(filteredSymbols[selectedIndex])
        } else if (symbol.trim()) {
          handleSubmit(e)
        }
        break
      case 'Escape':
        onClose()
        break
    }
  }

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (symbol.trim()) {
      onAddFavorite(symbol.toUpperCase())
      setSymbol('')
    }
  }

  const handleSymbolSelect = (selected: string) => {
    onAddFavorite(selected)
    setSymbol('')
  }

  return (
    <div className={styles.popupOverlay}>
      <div ref={popupRef} className={styles.popupContent}>
        <div className={styles.popupHeader}>
          <h3>Add Favorite Symbol</h3>
          <button
            onClick={onClose}
            className={styles.closeIconButton}
            aria-label="Close"
          >
            <XMarkIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.popupBody}>
          <form onSubmit={handleSubmit} className={styles.addForm}>
            <div className={styles.searchInputContainer}>
              <MagnifyingGlassIcon
                className={styles.popupSearchIcon}
                width={18}
                height={18}
              />
              <input
                ref={inputRef}
                type="text"
                value={symbol}
                onChange={e => setSymbol(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search or enter symbol (e.g. BTCUSDT)"
                className={styles.popupInput}
              />
            </div>
            <button type="submit" className={styles.addSymbolButton}>
              Add
            </button>
          </form>

          {filteredSymbols.length > 0 && (
            <div className={styles.suggestionsList}>
              {filteredSymbols.map((sym, index) => (
                <div
                  key={sym}
                  className={`${styles.suggestionItem} ${index === selectedIndex ? styles.selected : ''}`}
                  onClick={() => handleSymbolSelect(sym)}
                >
                  {sym}
                </div>
              ))}
            </div>
          )}

          {symbol && filteredSymbols.length === 0 && (
            <div className={styles.noResults}>
              No matching symbols found. You can still add "
              {symbol.toUpperCase()}" manually.
            </div>
          )}

          {currentFavorites.length > 0 && (
            <div className={styles.currentFavoritesSection}>
              <h4>Current Favorites</h4>
              <div className={styles.favoritesList}>
                {currentFavorites.map(fav => (
                  <div key={fav} className={styles.favoriteTag}>
                    {fav}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.popupFooter}>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
